import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid2 as Grid,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Button,
  TextField,
  IconButton,
  Stack,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useUserData } from "components/customHooks/userDataContext";
import { useLocation } from "react-router-dom";
import {
  getEncounterPrechartingDetails,
  updateEncounterPrechartNote,
} from "apiHandler/actions";
// import { updateEncounterPrechartingDetails } from "apiHandler/actions";
import { trimmedDate } from "utils/helper";

const hiddenJsonKeys = [
  "metadata",
  "prechart_note_html",
  "encounter_id",
  "patient_id",
  "generated_at",
];

const readOnlyEditKeys = ["patient_info"];

const Section = ({ title, children, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: "1px solid #e5e7eb",
      mb: 2,
      backgroundColor: "#fff",
    }}
  >
    <Typography fontWeight={700} fontSize={18} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2, opacity: 0.4 }} />
    {loading ? <Skeleton height={120} /> : children}
  </Paper>
);

const Field = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.8, lineHeight: 1.7 }}>
    <strong>{label}:</strong> {value || "—"}
  </Typography>
);

const formatTitle = (key = "") =>
  key
    .replace(/^_+/, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parsePreChartJson = (jsonString) => {
  try {
    if (!jsonString) return {};
    return JSON.parse(jsonString);
  } catch (error) {
    console.log("Invalid prechart_note_json:", error);
    return {};
  }
};

const deepClone = (value) => JSON.parse(JSON.stringify(value || {}));

const getByPath = (obj, path = []) =>
  path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);

const setByPath = (obj, path, value) => {
  const cloned = deepClone(obj);
  let current = cloned;

  path.forEach((key, index) => {
    if (index === path.length - 1) {
      current[key] = value;
    } else {
      current = current[key];
    }
  });

  return cloned;
};

const deleteByPath = (obj, path) => {
  const cloned = deepClone(obj);
  let current = cloned;

  path.forEach((key, index) => {
    if (index === path.length - 1) {
      if (Array.isArray(current)) {
        current.splice(key, 1);
      } else {
        delete current[key];
      }
    } else {
      current = current[key];
    }
  });

  return cloned;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  if (typeof value === "number") {
    if (value > 0 && value <= 1) return `${Math.round(value * 100)}%`;
    return String(value);
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  return String(value);
};

const TextBlock = ({ value }) => (
  <Typography
    component="pre"
    sx={{
      m: 0,
      whiteSpace: "pre-wrap",
      fontFamily: "inherit",
      fontSize: 14,
      lineHeight: 1.7,
      color: "#374151",
    }}
  >
    {formatValue(value)}
  </Typography>
);

const ObjectBlock = ({ data }) => (
  <Box>
    {Object.entries(data || {})
      .filter(([key]) => !key.startsWith("_"))
      .map(([key, value]) => (
        <Box key={key} sx={{ mb: 1.5 }}>
          {typeof value === "object" && value !== null ? (
            <>
              <Typography fontWeight={700} fontSize={15} sx={{ mb: 0.8 }}>
                {formatTitle(key)}
              </Typography>
              {renderDynamicValue(value)}
            </>
          ) : (
            <Field label={formatTitle(key)} value={formatValue(value)} />
          )}
        </Box>
      ))}
  </Box>
);

const ArrayBlock = ({ data }) => (
  <Box>
    {data.map((item, index) => (
      <Paper
        key={index}
        elevation={0}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          border: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
        }}
      >
        {typeof item === "object" && item !== null ? (
          <ObjectBlock data={item} />
        ) : (
          <TextBlock value={item} />
        )}
      </Paper>
    ))}
  </Box>
);

function renderDynamicValue(value) {
  if (value === null || value === undefined || value === "") {
    return <Typography color="text.secondary">—</Typography>;
  }

  if (Array.isArray(value)) {
    return value.length ? (
      <ArrayBlock data={value} />
    ) : (
      <Typography color="text.secondary">—</Typography>
    );
  }

  if (typeof value === "object") return <ObjectBlock data={value} />;

  return <TextBlock value={value} />;
}

const EditableValue = ({ label, value, path, editJson, setEditJson }) => {
  const handleChange = (newValue) => {
    let finalValue = newValue;

    if (typeof value === "number") {
      finalValue = newValue === "" ? "" : Number(newValue);
    }

    if (typeof value === "boolean") {
      finalValue = newValue === "true";
    }

    setEditJson((prev) => setByPath(prev, path, finalValue));
  };

  if (Array.isArray(value)) {
    return (
      <Box>
        <Typography fontWeight={700} fontSize={15} sx={{ mb: 1 }}>
          {label}
        </Typography>

        {value.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              position: "relative",
            }}
          >
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                setEditJson((prev) => deleteByPath(prev, [...path, index]))
              }
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            <Box sx={{ pr: 4 }}>
              <EditableValue
                label={`${label} ${index + 1}`}
                value={item}
                path={[...path, index]}
                editJson={editJson}
                setEditJson={setEditJson}
              />
            </Box>
          </Paper>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            const sample = value?.[0];
            const newItem =
              typeof sample === "object" && sample !== null
                ? Object.keys(sample).reduce((acc, key) => {
                    acc[key] =
                      typeof sample[key] === "number"
                        ? 0
                        : typeof sample[key] === "boolean"
                          ? false
                          : "";
                    return acc;
                  }, {})
                : "";

            setEditJson((prev) => {
              const currentArray = getByPath(prev, path) || [];
              return setByPath(prev, path, [...currentArray, newItem]);
            });
          }}
          sx={{ textTransform: "none" }}
        >
          Add {label}
        </Button>
      </Box>
    );
  }

  if (typeof value === "object" && value !== null) {
    return (
      <Box>
        {Object.entries(value)
          .filter(([key]) => !key.startsWith("_"))
          .map(([key, childValue]) => (
            <Box key={key} sx={{ mb: 2 }}>
              <EditableValue
                label={formatTitle(key)}
                value={childValue}
                path={[...path, key]}
                editJson={editJson}
                setEditJson={setEditJson}
              />
            </Box>
          ))}
      </Box>
    );
  }

  if (typeof value === "boolean") {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={String(value)}
        onChange={(e) => handleChange(e.target.value)}
        SelectProps={{ native: true }}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </TextField>
    );
  }

  return (
    <TextField
      fullWidth
      multiline={typeof value === "string" && value.length > 80}
      minRows={typeof value === "string" && value.length > 80 ? 4 : 1}
      size="small"
      label={label}
      type={typeof value === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      sx={{
        "& textarea": {
          lineHeight: 1.6,
        },
      }}
    />
  );
};

const PreChartTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preChartData, setPreChartData] = useState(null);
  const [editJson, setEditJson] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState("");

  const userData = useUserData();
  console.log("userData: ", userData);
  const token = userData?.access_token;

  const location = useLocation();
  const rowData = location.state?.data || {};

  useEffect(() => {
    getPreCharting();
  }, []);

  const getPreCharting = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getEncounterPrechartingDetails(
        { encounter_id: rowData.encounter_id || rowData.id },
        token,
      );

      if (res?.length) {
        setPreChartData(res[0]);

        const parsedJson = parsePreChartJson(res[0]?.prechart_note_json);
        setEditJson(parsedJson);
      } else {
        setPreChartData(null);
        setEditJson({});
      }
    } catch (error) {
      console.log("error: ", error);
      setError("Unable to load pre-charting notes.");
    } finally {
      setLoading(false);
    }
  };

  const preChartJson = useMemo(
    () => parsePreChartJson(preChartData?.prechart_note_json),
    [preChartData?.prechart_note_json],
  );

  const displayJson = isEditing ? editJson : preChartJson;

  const dynamicSections = useMemo(() => {
    return Object.entries(displayJson || {}).filter(
      ([key, value]) =>
        !key.startsWith("_") &&
        !hiddenJsonKeys.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== "",
    );
  }, [displayJson]);

  const patientName =
    rowData?.patient_name ||
    `${rowData?.first_name || ""} ${rowData?.last_name || ""}`.trim();

  const handleEdit = () => {
    setEditJson(deepClone(preChartJson));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditJson(deepClone(preChartJson));
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      const payload = {
        precharting_id: preChartData?.id,
        practice_id: preChartData?.practice_id || userData?.selected_practice,
        prechart_note_json: JSON.stringify(editJson),
        user_name: `${userData?.user_details?.user_name}`,
      };

      console.log("Save pre-chart payload:", payload);

      const res = await updateEncounterPrechartNote(payload, token);

      setPreChartData((prev) => ({
        ...prev,
        prechart_note_json: JSON.stringify(editJson),
        status: prev?.status || "Updated",
      }));

      if (res && res?.status == "Success") {
        setIsEditing(false);
        setSnackbar("Pre-charting notes updated successfully.");
        getPreCharting();
      }
    } catch (error) {
      console.log("error: ", error);
      setError("Unable to save pre-charting notes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={2}>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        {loading ? (
          <Skeleton width={220} height={32} />
        ) : (
          <Typography fontWeight={700} fontSize={20}>
            Pre Chart Note
          </Typography>
        )}

        <Box display="flex" alignItems="center" gap={1}>
          {preChartData?.status && (
            <Chip
              size="small"
              label={preChartData.status}
              color="primary"
              variant="outlined"
            />
          )}

          {!loading && preChartData && !isEditing && (
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Edit
            </Button>
          )}

          {!loading && preChartData && isEditing && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
                disabled={saving}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Cancel
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary">
            AI Pre-Chart Workflow
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Patient Details" loading={loading}>
            <Field
              label="Patient ID"
              value={
                displayJson?.patient_info?.patient_id ||
                preChartData?.patient_id ||
                rowData?.patient_id
              }
            />
            <Field label="Name" value={patientName} />
            <Field label="DOB" value={trimmedDate(rowData?.dob)} />
            <Field
              label="Encounter ID"
              value={
                displayJson?.encounter_id ||
                preChartData?.encounter_id ||
                rowData?.encounter_id ||
                rowData?.id
              }
            />
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Pre-Chart Status" loading={loading}>
            <Field label="Status" value={preChartData?.status} />
            <Field
              label="AI Confidence"
              value={
                preChartData?.ai_conf_score
                  ? `${preChartData.ai_conf_score}%`
                  : "—"
              }
            />
            <Field
              label="Missing Information"
              value={preChartData?.missing_information}
            />
            <Field label="Created By" value={preChartData?.created_by} />
          </Section>
        </Grid>
      </Grid>

      {dynamicSections.length ? (
        dynamicSections.map(([key, value]) => (
          <Section key={key} title={formatTitle(key)} loading={loading}>
            {isEditing && !readOnlyEditKeys.includes(key) ? (
              <EditableValue
                label={formatTitle(key)}
                value={value}
                path={[key]}
                editJson={editJson}
                setEditJson={setEditJson}
              />
            ) : (
              renderDynamicValue(value)
            )}
          </Section>
        ))
      ) : (
        <Section title="Generated Pre-Charting Notes" loading={loading}>
          <Typography color="text.secondary">
            No pre-charting notes found.
          </Typography>
        </Section>
      )}

      {preChartData?.ai_justification_notes && !isEditing && (
        <Section title="AI Justification Notes" loading={loading}>
          <TextBlock value={preChartData.ai_justification_notes} />
        </Section>
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Box>
  );
};

export default PreChartTab;
